import { HandlerDebriefChat, type ReportMessage } from "./handler-debrief-chat";
import { useEffect, useRef, useState } from "react";
import {
  Mic,
  Square,
  ArrowRight,
  Check,
  LoaderCircle,
  ChevronUp,
  X,
} from "lucide-react";
import { voiceErrorMessage } from "./voice-errors";
import { useStepperStore } from "@/store";
import { apiClient } from "@/shared/lib/api-client";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import type { Mission } from "@/features/missionkit/api/use-mission";
import { useVoiceAnswer } from "./use-voice-answer";
import { DebriefMessage } from "./debrief-message";
import "./voice-report.css";

export interface ReportDraft {
  whatIDid: string;
  howItFelt: string;
  reflectionOptional?: boolean;
}
export interface ExtraCredit {
  status: "offered" | "accepted" | "declined" | "completed";
  task: string;
  evidence?: string;
}
interface DebriefTurn {
  token: string;
  reply: string;
  complete: boolean;
  nextAction?: "ask" | "review";
  goalStatus: string;
  stage?: "what_happened" | "how_it_felt" | "review" | "extra_credit";
  missing: string[];
  report: ReportDraft;
  extraCredit?: ExtraCredit;
}

/** Riva supplies input; Stepper owns questions, facts and completion. Replies
 * are plain text now, so a later speech-output adapter needs no new turn API. */
export function VoiceReport({
  mission,
  onReview,
  onFallback,
  onPause,
}: {
  mission: Mission;
  onReview: (draft: ReportDraft) => void;
  onFallback?: (draft: ReportDraft) => void;
  onPause?: () => void;
}) {
  const adventureId = useStepperStore((state) => state.adventureId);
  const inlineTextMode = adventureId === 102; // ponytail: adventure102 only; others keep legacy fallback
  const stepName = useStepperStore((state) => state.currentStepName);
  const [sessionId] = useState(() => crypto.randomUUID());
  const initialRequest = useRef(crypto.randomUUID());
  const turnRequest = useRef(crypto.randomUUID());
  const [messages, setMessages] = useState<ReportMessage[]>([]);
  const [turn, setTurn] = useState<DebriefTurn | null>(null);
  const missionSheet = useRef<HTMLDialogElement>(null);
  const extraCreditPending =
    turn?.extraCredit && turn.extraCredit.status !== "completed";
  const readyForReview = turn?.complete === true && !extraCreditPending;
  const [answer, setAnswer] = useState("");
  const [hasTranscript, setHasTranscript] = useState(false);
  const [textMode, setTextMode] = useState(false);
  const [waiting, setWaiting] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const active = useRef(true);
  const pending = useRef(false);
  const controller = useRef<AbortController | null>(null);
  const appendRecording = useRef(false);
  const voice = useVoiceAnswer((text) => {
    setAnswer((previous) =>
      appendRecording.current
        ? [previous.trim(), text].filter(Boolean).join("\n\n")
        : text,
    );
    setHasTranscript(true);
  });

  useEffect(() => {
    active.current = true;
    const initial = new AbortController();
    controller.current = initial;
    apiClient
      .post<{ result: DebriefTurn }>(
        API_ENDPOINTS.mission.debrief,
        {
          missionId: mission.missionId,
          ...(mission.customId ? { customMissionId: mission.customId } : {}),
          adventureId,
          stepName,
          sessionId,
          requestId: initialRequest.current,
        },
        { signal: initial.signal, timeout: 85_000 },
      )
      .then(({ data }) => {
        if (!initial.signal.aborted) {
          setTurn(data.result);
          setMessages([{ text: data.result.reply, member: false }]);
        }
      })
      .catch(() => {
        if (!initial.signal.aborted)
          setError("Couldn't reach your debrief. Retry, or choose Can't talk.");
      })
      .finally(() => {
        if (!initial.signal.aborted) setWaiting(false);
      });
    return () => {
      active.current = false;
      initial.abort();
      controller.current?.abort();
    };
  }, [
    mission.missionId,
    mission.customId,
    adventureId,
    stepName,
    sessionId,
    attempt,
  ]);

  const send = async () => {
    if (!turn || !answer.trim() || voice.busy || pending.current || waiting)
      return;
    pending.current = true;
    setWaiting(true);
    setError("");
    const next = new AbortController();
    controller.current = next;
    const text = answer.trim();
    const wasTranscript = hasTranscript;
    setAnswer("");
    setHasTranscript(false);
    setMessages((previous) => [...previous, { text, member: true }]);
    try {
      const { data } = await apiClient.post<{ result: DebriefTurn }>(
        API_ENDPOINTS.mission.debrief,
        {
          missionId: mission.missionId,
          ...(mission.customId ? { customMissionId: mission.customId } : {}),
          adventureId,
          stepName,
          sessionId,
          requestId: turnRequest.current,
          token: turn.token,
          message: text,
        },
        { signal: next.signal, timeout: 85_000 },
      );
      if (!active.current || next.signal.aborted) return;
      setTurn(data.result);
      setMessages((previous) => [
        ...previous,
        { text: data.result.reply, member: false },
      ]);
      turnRequest.current = crypto.randomUUID();
      setAnswer("");
      setHasTranscript(false);
    } catch (err) {
      if (!active.current || next.signal.aborted) return;
      setAnswer(text);
      setHasTranscript(wasTranscript);
      setMessages((previous) => previous.slice(0, -1));
      setError(voiceErrorMessage(err, "debrief"));
    } finally {
      pending.current = false;
      if (active.current && !next.signal.aborted) setWaiting(false);
    }
  };

  const fallback = () => {
    void apiClient
      .post(
        API_ENDPOINTS.mission.debrief,
        {
          missionId: mission.missionId,
          ...(mission.customId ? { customMissionId: mission.customId } : {}),
          adventureId,
          stepName,
          sessionId,
          requestId: crypto.randomUUID(),
          token: turn?.token ?? "",
          event: "CANTTALK",
        },
        { timeout: 85_000 },
      )
      .catch(() => {});
    // Use classified report facts so communication preferences never become post text.
    // Keep the unsent answer editable in the written draft.
    onFallback?.({
      whatIDid: [turn?.report.whatIDid, answer.trim()]
        .filter((text, index, all) => text && all.indexOf(text) === index)
        .join("\n\n"),
      howItFelt: turn?.report.howItFelt || "",
      ...(turn?.report.reflectionOptional ? { reflectionOptional: true } : {}),
    });
  };

  const recordAnswer = (append: boolean) => {
    if (voice.busy || waiting || pending.current) return;
    appendRecording.current = append;
    void voice.start();
  };

  const recording = voice.status === "recording";
  const reviewingAnswer = hasTranscript && !voice.busy;
  const processing =
    waiting || voice.status === "transcribing" || voice.status === "requesting";
  const status =
    error || voice.error
      ? "Connection interrupted"
      : recording
        ? "Recording your answer"
        : voice.status === "transcribing"
          ? "Processing audio"
          : voice.status === "requesting"
            ? "Opening microphone"
            : waiting
              ? turn
                ? "Awaiting reply"
                : `Connecting to ${mission.customId && mission.handler ? mission.handler : "Control"}`
              : turn?.complete
                ? "Debrief complete"
                : hasTranscript
                  ? "Answer ready to send"
                  : "Your turn";
  const finish = () => {
    if (turn?.complete) onReview(turn.report);
  };

  if (mission.customId && mission.handler)
    return (
      <HandlerDebriefChat
        name={mission.handler}
        messages={messages}
        waiting={waiting}
        recording={recording}
        busy={voice.busy}
        error={error || voice.error || ""}
        answer={answer}
        setAnswer={setAnswer}
        hasTranscript={hasTranscript}
        record={recordAnswer}
        stop={voice.stop}
        send={() => void send()}
        complete={readyForReview}
        finish={finish}
        connected={!!turn}
        levels={voice.audioLevels ?? []}
        retry={() => {
          setWaiting(true);
          setError("");
          setAttempt((value) => value + 1);
        }}
        extraCredit={turn?.extraCredit}
        onPause={onPause}
      />
    );

  return (
    <section
      className="voice-debrief"
      aria-label="Spoken debrief"
      data-active={processing}
      data-recording={recording}
    >
      <header className="debrief-header">
        <h2>
          {mission.customId && mission.handler ? mission.handler : "CONTROL"}
        </h2>
      </header>
      <div className="debrief-body">
        <div
          className="debrief-display"
          aria-busy={waiting}
          style={{
            fontSize:
              (turn?.reply.length ?? 0) > 200
                ? "21.5px"
                : (turn?.reply.length ?? 0) > 125
                  ? "28.5px"
                  : "34px",
          }}
        >
          {turn ? (
            <DebriefMessage key={turn.token} text={turn.reply} />
          ) : (
            <p className="debrief-opening">Establishing contact…</p>
          )}
          <div
            className={processing || recording ? "debrief-status" : "sr-only"}
            role="status"
          >
            {processing ? (
              <LoaderCircle
                className="debrief-spinner"
                size={14}
                aria-hidden="true"
              />
            ) : turn?.complete ? (
              <Check size={14} aria-hidden="true" />
            ) : (
              <span className="debrief-led" data-recording={recording} />
            )}
            {status}
            {recording && ` · ${voice.seconds}s`}
          </div>
        </div>
        {extraCreditPending && (
          <div className="debrief-extra-credit" role="status">
            <strong>Extra Credit</strong>
            <p>{turn!.extraCredit!.task}</p>
          </div>
        )}
        {!turn?.complete &&
          !readyForReview &&
          !textMode &&
          !reviewingAnswer && (
            <button
              type="button"
              className="debrief-talk"
              data-recording={recording}
              disabled={processing}
              aria-label={
                recording
                  ? "Stop recording"
                  : answer
                    ? "Record answer again"
                    : "Record answer"
              }
              onClick={() => {
                if (recording) voice.stop();
                else recordAnswer(false);
              }}
            >
              <div className="debrief-signal" aria-hidden="true">
                {Array.from({ length: 32 }, (_, i) => {
                  const levels = voice.audioLevels ?? [];
                  const level = recording
                    ? (levels[i - (32 - levels.length)] ?? 0)
                    : 0;
                  return (
                    <i
                      key={i}
                      style={{
                        animationDelay: `${i * 53}ms`,
                        height: `${3 + level * 53}px`,
                      }}
                    />
                  );
                })}
              </div>
              <span className="debrief-talk-icon">
                {recording ? (
                  <Square aria-hidden="true" />
                ) : (
                  <Mic aria-hidden="true" />
                )}
              </span>
              <span>
                {recording
                  ? "Stop recording"
                  : voice.status === "transcribing"
                    ? "Transcribing…"
                    : voice.status === "requesting"
                      ? "Opening microphone…"
                      : answer
                        ? "Record answer again"
                        : "Record"}
              </span>
            </button>
          )}
        {!turn?.complete && !readyForReview && textMode && turn && (
          <div className="debrief-text-compose">
            <textarea
              aria-label="Your reply"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={2}
              placeholder="Type your answer…"
              disabled={waiting}
            />
            <button
              type="button"
              className="debrief-primary"
              onClick={() => void send()}
              disabled={waiting || !answer.trim()}
            >
              Send answer <ArrowRight size={17} aria-hidden="true" />
            </button>
          </div>
        )}
        {!turn?.complete && (
          <button
            type="button"
            className="debrief-fallback"
            onClick={() =>
              inlineTextMode ? setTextMode(!textMode) : fallback()
            }
          >
            {textMode && inlineTextMode ? "Use voice" : "Can't talk"}
          </button>
        )}
        {onPause && !turn?.complete && (
          <button type="button" className="debrief-fallback" onClick={onPause}>
            I'll come back
          </button>
        )}
        {(error || voice.error) && (
          <p role="alert" className="debrief-error">
            {error || voice.error}
          </p>
        )}
        {!turn && !waiting && (
          <button
            type="button"
            className="debrief-primary"
            onClick={() => {
              setWaiting(true);
              setError("");
              setAttempt((value) => value + 1);
            }}
          >
            Retry debrief
          </button>
        )}
        {turn?.complete || readyForReview ? (
          <div className="debrief-complete">
            <Check size={20} aria-hidden="true" />
            <div>
              <strong>Ready to sign off.</strong>
              <p>
                End your debrief to review your draft. You can edit it and add
                evidence before filing.
              </p>
            </div>
          </div>
        ) : (
          turn && (
            <>
              {reviewingAnswer && !textMode && (
                <label className="debrief-answer">
                  Check your answer
                  <textarea
                    aria-label="Check your answer"
                    value={answer}
                    disabled={voice.busy || waiting}
                    onChange={(event) => setAnswer(event.target.value)}
                    rows={3}
                  />
                </label>
              )}
              {reviewingAnswer && !textMode && (
                <div className="debrief-answer-actions">
                  <button
                    type="button"
                    className="debrief-primary"
                    onClick={() => void send()}
                    disabled={voice.busy || waiting || !answer.trim()}
                  >
                    Send answer <ArrowRight size={17} aria-hidden="true" />
                  </button>
                  <div className="debrief-recording-actions">
                    <button
                      type="button"
                      onClick={() => recordAnswer(false)}
                      disabled={voice.busy || waiting}
                    >
                      Re-record
                    </button>
                    <button
                      type="button"
                      onClick={() => recordAnswer(true)}
                      disabled={voice.busy || waiting}
                    >
                      Add more
                    </button>
                  </div>
                </div>
              )}
            </>
          )
        )}
      </div>
      <footer className="debrief-footer">
        {readyForReview && (
          <button
            type="button"
            className="debrief-primary"
            disabled={voice.busy || waiting}
            onClick={finish}
          >
            End debrief <ArrowRight size={16} aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          className="debrief-mission-toggle"
          onClick={() => missionSheet.current?.showModal()}
          aria-haspopup="dialog"
        >
          <ChevronUp size={14} aria-hidden="true" /> View mission
        </button>
      </footer>
      <dialog
        ref={missionSheet}
        className="debrief-mission-sheet"
        aria-labelledby="debrief-mission-title"
        onClick={(event) => {
          if (event.target === event.currentTarget)
            missionSheet.current?.close();
        }}
      >
        <div className="debrief-mission-content">
          <button
            type="button"
            className="debrief-mission-close"
            aria-label="Close mission"
            onClick={() => missionSheet.current?.close()}
          >
            <X size={22} />
          </button>
          <span className="debrief-mission-eyebrow">YOUR MISSION</span>
          <h2 id="debrief-mission-title">{mission.name}</h2>
          <p>{mission.mission}</p>
          <button
            type="button"
            className="debrief-primary"
            onClick={() => missionSheet.current?.close()}
          >
            Back to{" "}
            {mission.customId && mission.handler ? mission.handler : "Control"}
          </button>
        </div>
      </dialog>
    </section>
  );
}
